import React, { useEffect, useState } from 'react';
import api from '../api';

const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const FREQ_OPTIONS = [
  { value: 'always',  label: 'À chaque hausse de dépenses' },
  { value: 'daily',   label: 'Maximum 1 fois par jour' },
  { value: 'weekly',  label: 'Maximum 1 fois par semaine' },
  { value: 'once',    label: 'Une seule fois par seuil' },
];

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
        background: value ? 'var(--primary)' : 'var(--border)',
        position: 'relative', transition: 'background .2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left .2s',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 16 }}>
      <span style={{ fontSize: 13, color: 'var(--text-soft)', flex: 1 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>
    </div>
  );
}

const sel = {
  padding: '6px 10px', borderRadius: 8,
  border: '1px solid var(--border)',
  fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
};

export default function NotificationSettings() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving]  = useState(false);
  const [message, setMessage] = useState(null);
  const [newThreshold, setNewThreshold] = useState('');

  useEffect(() => {
    api.get('/notifications/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const set = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  const addThreshold = () => {
    const v = parseInt(newThreshold);
    if (!v || v < 1 || v > 200) return;
    const next = [...new Set([...(config.budget_thresholds || []), v])].sort((a, b) => a - b);
    set('budget_thresholds', next);
    setNewThreshold('');
  };

  const removeThreshold = (t) => {
    set('budget_thresholds', config.budget_thresholds.filter(x => x !== t));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.put('/notifications/config', config);
      setConfig(r.data);
      setMessage({ type: 'success', text: 'Configuration enregistrée.' });
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erreur' });
    }
    setSaving(false);
  };

  if (!config) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div style={{ maxWidth: 560, padding: '24px 0' }}>
      <h3 style={{ margin: '0 0 20px' }}>Notifications</h3>

      {message && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color:      message.type === 'success' ? '#166534' : '#991b1b',
          border:     `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
        }}>{message.text}</div>
      )}

      {/* Alertes budget */}
      <Section title="Alertes budget">
        <Row label="Activer les alertes budget">
          <Toggle value={config.budget_enabled} onChange={v => set('budget_enabled', v)} />
        </Row>

        {config.budget_enabled && (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>Seuils (%)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {(config.budget_thresholds || []).map(t => (
                  <span key={t} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                    background: t >= 100 ? '#fef2f2' : t >= 75 ? '#fffbeb' : '#f0fdf4',
                    color: t >= 100 ? '#dc2626' : t >= 75 ? '#d97706' : '#16a34a',
                    border: `1px solid ${t >= 100 ? '#fca5a5' : t >= 75 ? '#fde68a' : '#86efac'}`,
                  }}>
                    {t}%
                    <button onClick={() => removeThreshold(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, color: 'inherit', lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number" min="1" max="200" placeholder="Ex: 60"
                  value={newThreshold}
                  onChange={e => setNewThreshold(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addThreshold()}
                  style={{ ...sel, width: 90 }}
                />
                <button onClick={addThreshold} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--primary)', color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  + Ajouter
                </button>
              </div>
            </div>

            <Row label="Fréquence des alertes">
              <select style={sel} value={config.budget_frequency} onChange={e => set('budget_frequency', e.target.value)}>
                {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Row>
          </>
        )}
      </Section>

      {/* Résumé hebdo */}
      <Section title="Résumé hebdomadaire">
        <Row label="Activer le résumé hebdo">
          <Toggle value={config.weekly_summary_enabled} onChange={v => set('weekly_summary_enabled', v)} />
        </Row>
        {config.weekly_summary_enabled && (
          <>
            <Row label="Jour">
              <select style={sel} value={config.weekly_summary_day} onChange={e => set('weekly_summary_day', Number(e.target.value))}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </Row>
            <Row label="Heure">
              <select style={sel} value={config.weekly_summary_hour} onChange={e => set('weekly_summary_hour', Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}h00</option>
                ))}
              </select>
            </Row>
          </>
        )}
      </Section>

      {/* Sync */}
      <Section title="Synchronisation bancaire">
        <Row label="Notifier après chaque sync">
          <Toggle value={config.sync_notify_enabled} onChange={v => set('sync_notify_enabled', v)} />
        </Row>
      </Section>

      {/* Solde bancaire */}
      <Section title="Alerte solde bancaire">
        <Row label="Activer les alertes solde">
          <Toggle value={config.balance_alert_enabled} onChange={v => set('balance_alert_enabled', v)} />
        </Row>
        {config.balance_alert_enabled && (
          <>
            <Row label="Alerte si solde comptabilisé < (€)">
              <input
                type="number" min="0"
                value={config.balance_clbd_threshold ?? ''}
                onChange={e => set('balance_clbd_threshold', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="Ex: 500"
                style={{ ...sel, width: 100 }}
              />
            </Row>
            <Row label="Alerte si mouvements à venir < (€)">
              <input
                type="number" min="0"
                value={config.balance_xpcd_threshold ?? ''}
                onChange={e => set('balance_xpcd_threshold', e.target.value === '' ? null : Number(e.target.value))}
                placeholder="Ex: 300"
                style={{ ...sel, width: 100 }}
              />
            </Row>
            <Row label="Fréquence">
              <select style={sel} value={config.balance_frequency} onChange={e => set('balance_frequency', e.target.value)}>
                <option value="always">À chaque sync</option>
                <option value="daily">Maximum 1 fois par jour</option>
                <option value="weekly">Maximum 1 fois par semaine</option>
              </select>
            </Row>
          </>
        )}
      </Section>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: '10px 24px', borderRadius: 8,
          background: saving ? 'var(--border)' : 'var(--primary)',
          color: '#fff', border: 'none', fontWeight: 600, fontSize: 14,
          cursor: saving ? 'wait' : 'pointer',
        }}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  );
}
